import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, IconButton, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, TextField
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import {
  getBrandSubsidiaryMappings,
  upsertBrandSubsidiaryMapping,
  deleteBrandSubsidiaryMapping,
  getWarehouses
} from '../../services/inventoryService';
import api from '../../config/api';
import { excludeCompanyLevel } from '../../utils/brandFilters';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.superchargeme.com';

/**
 * WarehouseBrandSubsidiaryMatrix — admin UI for (warehouse_code, brand) → subsidiary overrides.
 *
 * Used when a warehouse's default subsidiary doesn't apply for a specific brand.
 * Example: ITC warehouse maps to Excite Foundry Ltd by default, but for the
 * S&S/eb5/SkinnyMint/BodyBoss brands it maps to Life O Inc.
 */
export default function WarehouseBrandSubsidiaryMatrix() {
  const [mappings, setMappings] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [brands, setBrands] = useState([]);
  const [subsidiaries, setSubsidiaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    warehouse_code: '',
    brand_id: '',
    subsidiary_id: '',
    notes: ''
  });

  const load = async () => {
    try {
      setLoading(true);
      const [mRes, wRes, bRes, sRes] = await Promise.all([
        getBrandSubsidiaryMappings(),
        getWarehouses({ limit: 100 }),
        api.get(`${API_BASE}/api/brands`).then(r => r.data),
        api.get(`${API_BASE}/api/subsidiaries`).then(r => r.data).catch(() => ({ data: [] }))
      ]);
      // Each endpoint returns a slightly different envelope. Normalize to array
      // and guard with Array.isArray to avoid storing the envelope object itself
      // (which would crash .map() downstream — bit us once already).
      const pickArr = (...candidates) => {
        for (const c of candidates) if (Array.isArray(c)) return c;
        return [];
      };
      setMappings(pickArr(mRes?.data, mRes));
      setWarehouses(pickArr(wRes?.warehouses, wRes?.data, wRes));
      setBrands(pickArr(bRes?.brands, bRes?.data, bRes));
      setSubsidiaries(pickArr(sRes?.subsidiaries, sRes?.data, sRes));
      setError(null);
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleOpen = (row = null) => {
    if (row) {
      setForm({
        id: row.id,
        warehouse_code: row.warehouse_code,
        brand_id: row.brand_id,
        subsidiary_id: row.subsidiary_id,
        notes: row.notes || ''
      });
    } else {
      setForm({ warehouse_code: '', brand_id: '', subsidiary_id: '', notes: '' });
    }
    setError(null); setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      await upsertBrandSubsidiaryMapping(form);
      setDialogOpen(false);
      await load();
    } catch (e) {
      setError(e.message || 'Save failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this mapping?')) return;
    try {
      await deleteBrandSubsidiaryMapping(id);
      await load();
    } catch (e) {
      setError(e.message || 'Delete failed');
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="h6">Warehouse × Brand → Subsidiary Overrides</Typography>
              <Typography variant="body2" color="textSecondary">
                When a warehouse's default subsidiary doesn't apply for a specific brand.
                Example: ITC default is Excite Foundry Ltd; override to Life O Inc for US-routed brands.
              </Typography>
            </Box>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
              Add Override
            </Button>
          </Box>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Warehouse</TableCell>
                  <TableCell>Brand</TableCell>
                  <TableCell>Subsidiary</TableCell>
                  <TableCell>Effective From</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mappings.length === 0 ? (
                  <TableRow><TableCell colSpan={6} align="center">
                    <Typography color="textSecondary" sx={{ py: 2 }}>No overrides configured. Defaults from warehouse.subsidiary_id apply.</Typography>
                  </TableCell></TableRow>
                ) : mappings.map(m => (
                  <TableRow key={m.id} hover>
                    <TableCell>
                      <Chip label={m.warehouse_code} size="small" />
                      {m.warehouse_name && <Typography variant="caption" display="block">{m.warehouse_name}</Typography>}
                    </TableCell>
                    <TableCell>{m.brand_name || `Brand #${m.brand_id}`}</TableCell>
                    <TableCell><strong>{m.subsidiary_name || `Subsidiary #${m.subsidiary_id}`}</strong></TableCell>
                    <TableCell>{m.effective_from}</TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ maxWidth: 300, display: 'inline-block' }}>{m.notes}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleOpen(m)}><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => handleDelete(m.id)}><DeleteIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{form.id ? 'Edit' : 'Add'} Override</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <FormControl fullWidth>
              <InputLabel>Warehouse</InputLabel>
              <Select
                value={form.warehouse_code}
                label="Warehouse"
                onChange={e => setForm({ ...form, warehouse_code: e.target.value })}
              >
                {warehouses.map(w => (
                  <MenuItem key={w.code || w.id} value={w.code}>{w.code} — {w.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Brand</InputLabel>
              <Select
                value={form.brand_id}
                label="Brand"
                onChange={e => setForm({ ...form, brand_id: e.target.value })}
              >
                {excludeCompanyLevel(brands).map(b => (
                  <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Subsidiary</InputLabel>
              <Select
                value={form.subsidiary_id}
                label="Subsidiary"
                onChange={e => setForm({ ...form, subsidiary_id: e.target.value })}
              >
                {subsidiaries.map(s => (
                  <MenuItem key={s.id} value={s.id}>{s.legal_name || s.name} ({s.currency || s.country})</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Notes"
              multiline
              minRows={2}
              value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!form.warehouse_code || !form.brand_id || !form.subsidiary_id}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
